import { useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { formatAUD, getFinancialYear, getFinancialYearStart, getFinancialYearEnd } from '@/lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { Receipt } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

const CHART_COLORS = [
  'hsl(168, 55%, 38%)',
  'hsl(43, 90%, 55%)',
  'hsl(0, 72%, 51%)',
  'hsl(200, 60%, 50%)',
  'hsl(280, 50%, 55%)',
  'hsl(152, 60%, 42%)',
  'hsl(30, 80%, 55%)',
  'hsl(340, 60%, 50%)',
  'hsl(190, 70%, 45%)',
  'hsl(60, 70%, 50%)',
];

export default function ReportsPage() {
  const { data } = useAuth();
  const now = new Date();
  const [period, setPeriod] = useState<'month' | 'fy'>('month');

  const filtered = useMemo(() => {
    if (period === 'month') {
      const y = now.getFullYear();
      const m = now.getMonth();
      return data.transactions.filter(t => {
        const d = new Date(t.date);
        return d.getFullYear() === y && d.getMonth() === m;
      });
    }
    const start = getFinancialYearStart(now);
    const end = getFinancialYearEnd(now);
    return data.transactions.filter(t => {
      const d = new Date(t.date);
      return d >= start && d <= end;
    });
  }, [data.transactions, period]);

  // Spending by category (expenses only)
  const spendingData = useMemo(() => {
    const map: Record<string, number> = {};
    filtered.filter(t => t.type === 'expense').forEach(t => {
      map[t.category] = (map[t.category] || 0) + t.amount;
    });
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filtered]);

  // Income vs Expenses by month
  const monthlyData = useMemo(() => {
    const map: Record<string, { month: string; income: number; expenses: number }> = {};
    filtered.forEach(t => {
      const d = new Date(t.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map[key]) map[key] = { month: key, income: 0, expenses: 0 };
      if (t.type === 'income') map[key].income += t.amount;
      else map[key].expenses += t.amount;
    });
    return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
  }, [filtered]);

  // Format month labels
  const formattedMonthlyData = useMemo(() =>
    monthlyData.map(d => ({
      ...d,
      label: new Date(d.month + '-01').toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }),
    })),
  [monthlyData]);

  // Tax deductible summary
  const taxSummary = useMemo(() => {
    const deductible = filtered.filter(t => t.type === 'expense' && t.taxDeductible);
    const byCategory: Record<string, number> = {};
    let total = 0;
    deductible.forEach(t => {
      byCategory[t.category] = (byCategory[t.category] || 0) + t.amount;
      total += t.amount;
    });
    return {
      total,
      categories: Object.entries(byCategory)
        .map(([name, amount]) => ({ name, amount }))
        .sort((a, b) => b.amount - a.amount),
    };
  }, [filtered]);

  // Super contributions
  const totalSuper = useMemo(() =>
    filtered
      .filter(t => t.type === 'income' && t.superContribution)
      .reduce((sum, t) => sum + (t.superContribution || 0), 0),
  [filtered]);

  // Budget vs Actual (current month only)
  const budgetVsActual = useMemo(() => {
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const monthBudgets = (data.budgets ?? []).filter(b => b.month === currentMonth);
    if (monthBudgets.length === 0) return [];
    const monthExpenses = data.transactions.filter(t => t.type === 'expense' && t.date.startsWith(currentMonth));
    return monthBudgets.map(b => {
      const spent = monthExpenses.filter(t => t.category === b.category).reduce((s, t) => s + t.amount, 0);
      return { category: b.category, budget: b.monthlyLimit, spent, pct: b.monthlyLimit > 0 ? (spent / b.monthlyLimit) * 100 : 0 };
    }).sort((a, b) => b.pct - a.pct);
  }, [data.budgets, data.transactions]);

  // Budget adherence trend (last 6 months)
  const adherenceTrend = useMemo(() => {
    const budgets = data.budgets ?? [];
    if (budgets.length === 0) return [];
    const months: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return months
      .map(m => {
        const mb = budgets.filter(b => b.month === m);
        if (mb.length === 0) return null;
        const totalBudget = mb.reduce((s, b) => s + b.monthlyLimit, 0);
        const totalSpent = data.transactions
          .filter(t => t.type === 'expense' && t.date.startsWith(m) && mb.some(b => b.category === t.category))
          .reduce((s, t) => s + t.amount, 0);
        const adherence = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;
        const label = new Date(m + '-01').toLocaleDateString('en-AU', { month: 'short' });
        return { month: m, label, adherence, budget: totalBudget, spent: totalSpent };
      })
      .filter(Boolean) as { month: string; label: string; adherence: number; budget: number; spent: number }[];
  }, [data.budgets, data.transactions]);

  const totalIncome = filtered.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
  const totalExpenses = filtered.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);

  const chartConfig = {
    income: { label: 'Income', color: 'hsl(152, 60%, 42%)' },
    expenses: { label: 'Expenses', color: 'hsl(0, 72%, 51%)' },
  };

  const periodLabel = period === 'month'
    ? now.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })
    : `FY ${getFinancialYear(now)}`;

  return (
    <div className="px-4 pt-6 pb-4">
      <h1 className="mb-4 font-display text-2xl font-bold">Reports & Charts</h1>

      <Tabs value={period} onValueChange={v => setPeriod(v as 'month' | 'fy')} className="mb-4">
        <TabsList className="w-full">
          <TabsTrigger value="month" className="flex-1">This Month</TabsTrigger>
          <TabsTrigger value="fy" className="flex-1">Financial Year</TabsTrigger>
        </TabsList>
      </Tabs>

      <p className="mb-4 text-sm font-medium text-muted-foreground">{periodLabel}</p>

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-3 gap-2">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Income</p>
            <p className="font-display text-sm font-bold text-success">{formatAUD(totalIncome)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Expenses</p>
            <p className="font-display text-sm font-bold text-destructive">{formatAUD(totalExpenses)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xs text-muted-foreground">Net</p>
            <p className="font-display text-sm font-bold">{formatAUD(totalIncome - totalExpenses)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Spending Breakdown Pie Chart */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Spending Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {spendingData.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No expenses recorded</p>
          ) : (
            <>
              <ChartContainer config={{}} className="mx-auto aspect-square max-h-[250px]">
                <PieChart>
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Pie
                    data={spendingData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {spendingData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ChartContainer>
              <div className="mt-3 flex flex-wrap gap-2">
                {spendingData.slice(0, 6).map((d, i) => (
                  <div key={d.name} className="flex items-center gap-1.5 text-xs">
                    <div
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                    />
                    <span className="text-muted-foreground">{d.name}</span>
                    <span className="font-medium">{formatAUD(d.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Income vs Expenses Bar Chart */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Income vs Expenses</CardTitle>
        </CardHeader>
        <CardContent>
          {formattedMonthlyData.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No data yet</p>
          ) : (
            <ChartContainer config={chartConfig} className="aspect-[4/3] max-h-[250px]">
              <BarChart data={formattedMonthlyData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar dataKey="income" fill="var(--color-income)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" fill="var(--color-expenses)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          )}
        </CardContent>
      </Card>

      {/* Budget vs Actual */}
      {budgetVsActual.length > 0 && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Budget vs Actual (This Month)</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                budget: { label: 'Budget', color: 'hsl(200, 60%, 50%)' },
                spent: { label: 'Spent', color: 'hsl(0, 72%, 51%)' },
              }}
              className="aspect-[4/3] max-h-[250px]"
            >
              <BarChart data={budgetVsActual} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={80} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                <Bar dataKey="budget" fill="var(--color-budget)" radius={[0, 4, 4, 0]} />
                <Bar dataKey="spent" fill="var(--color-spent)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ChartContainer>
            <div className="mt-4 space-y-2">
              {budgetVsActual.map(b => (
                <div key={b.category}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-medium">{b.category}</span>
                    <span className="text-muted-foreground">
                      {formatAUD(b.spent)} / {formatAUD(b.budget)}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(b.pct, 100)}
                    className={cn(
                      'h-1.5',
                      b.pct > 100 ? '[&>div]:bg-destructive' : b.pct > 80 ? '[&>div]:bg-warning' : '[&>div]:bg-success'
                    )}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Adherence Trend */}
      {adherenceTrend.length > 1 && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Budget Adherence Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-2 text-xs text-muted-foreground">
              % of total budget spent each month — 100% = on target
            </p>
            <ChartContainer
              config={{ adherence: { label: 'Adherence %', color: 'hsl(200, 60%, 50%)' } }}
              className="aspect-[4/3] max-h-[220px]"
            >
              <LineChart data={adherenceTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}%`} domain={[0, 'auto']} />
                <ChartTooltip
                  content={<ChartTooltipContent formatter={(value) => `${value}%`} />}
                />
                <ReferenceLine y={100} stroke="hsl(0, 72%, 51%)" strokeDasharray="4 4" label={{ value: '100%', position: 'right', fontSize: 10, fill: 'hsl(0, 72%, 51%)' }} />
                <Line
                  type="monotone"
                  dataKey="adherence"
                  stroke="var(--color-adherence)"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      {/* Tax Deductible Summary */}
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-4 w-4" />
            Tax Deductible Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          {taxSummary.categories.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No tax-deductible expenses
            </p>
          ) : (
            <div className="space-y-2">
              {taxSummary.categories.map(c => (
                <div key={c.name} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{c.name}</span>
                  <span className="font-medium">{formatAUD(c.amount)}</span>
                </div>
              ))}
              <div className="border-t pt-2">
                <div className="flex items-center justify-between font-display font-bold">
                  <span>Total Deductible</span>
                  <span>{formatAUD(taxSummary.total)}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Super Contributions */}
      {totalSuper > 0 && (
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <span className="text-sm text-muted-foreground">Super Contributions</span>
            <span className="font-display font-bold">{formatAUD(totalSuper)}</span>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
